import { GenericModal } from '@/components/Modal';
import { Button } from 'react-daisyui';
import { useState, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import { Spinner } from '@/components/Spinner';
import type { Folder, FirmClients } from '@types';
import { FilePermissions } from '@/components/FileBrowser/types';
import { getFormDataSignal } from '@/components/FileBrowser/utils';

interface Props {
  isOpen: boolean;
  folder: Folder;
  onClose: () => void;
  onSave: () => Promise<void>;
  firmClientsData?: FirmClients[];
  firmUserIds?: number[];
}

export default function GrantPermission({
  isOpen,
  folder,
  onClose,
  onSave,
  firmClientsData,
  firmUserIds,
}: Props) {
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedFirms, setSelectedFirms] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const originallyAllowedUsers = useMemo(() => {
    const users =
      folder.permissions
        ?.find((t) => t.fileId === folder.id)
        ?.allowed_users_by_user_name?.filter(
          (u) => !firmUserIds?.includes(u.user_id)
        ) || [];
    return users;
  }, [folder, firmUserIds]);

  const originallyAllowedFirms = useMemo(() => {
    const firmIds = folder.permissions?.find(
      (t) => t.fileId === folder.id
    )?.allowedFirms;

    // Find corresponding firm names using firmClientsData
    const firmsWithNames =
      firmIds
        ?.filter((firmId) =>
          firmClientsData?.some((fc) => fc.firm_id === firmId)
        )
        ?.map((firmId) => {
          const firmClient = firmClientsData?.find(
            (fc) => fc.firm_id === firmId
          );
          return {
            firmId,
            firmName: firmClient?.firm?.firm_name,
          };
        }) || [];

    return firmsWithNames;
  }, [folder, firmClientsData]);

  // const tagsAllowdedInFolder = folder.permissions?.find(t => t.fileId === folder.id)?.allowed_tags_by_user_name;

  const addOrRemovefirms = (firmId: number) => {
    if (selectedFirms.includes(firmId)) {
      setSelectedFirms(selectedFirms.filter((f) => f !== firmId));
    } else {
      setSelectedFirms([...selectedFirms, firmId]);
    }
  };

  const addOrRemoveUsers = (user: { user_id: number; user_name: string }) => {
    if (selectedUsers.includes(user.user_id)) {
      setSelectedUsers(selectedUsers.filter((u) => u !== user.user_id));
    } else {
      setSelectedUsers([...selectedUsers, user.user_id]);
    }
  };

  const handleShareWithAllUsers = () => {
    if (selectedUsers.length === originallyAllowedUsers?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(originallyAllowedUsers?.map((u) => u.user_id) || []);
    }
  };

  const handleShareWithAllFirms = () => {
    if (selectedFirms.length === originallyAllowedFirms?.length) {
      setSelectedFirms([]);
    } else {
      setSelectedFirms(originallyAllowedFirms?.map((f) => f.firmId) || []);
    }
  };

  const saveData = async () => {
    setIsSaving(true);
    const formData = getFormDataSignal() as FormData;
    const permissions: FilePermissions = {
      allowedUsers: selectedUsers.map((u) => u),
      allowedTags:
        folder.permissions?.find((t) => t.fileId === folder.id)?.allowedTags ||
        [],
      parentFolderId: folder.id,
      allowedFirms: selectedFirms.map((firmId) => ({
        firmId,
        skippedUsers: [], // we might want to handle this dynamically when the time comes.
      })),
    };
    formData.append('permissions', JSON.stringify(permissions));
    try {
      await onSave();
      setIsSaving(false);
      setSelectedUsers([]);
      setSelectedFirms([]);
      onClose();
    } catch (err) {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal isOpen={isOpen} onClose={onClose}>
      <>
        <ToastContainer />
        <div className='flex h-full flex-col overflow-hidden'>
          <div className='flex items-center'>
            <div className='w-full'>
              <div className='font-bold text-base'>
                This folder has been shared with external parties
              </div>
              <br />
              <div className='text-base px-4'>
                Would you like to share this file with the same parties who have
                access to the folder?
              </div>
            </div>
          </div>
          <br />

          <div className='flex'>
            <div className='flex items-start' key='div-main-all-users'>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <label
                className='flex items-center cursor-pointer'
                htmlFor='enable-tab'
                key='label-tab-all-users'
                onClick={() => handleShareWithAllUsers()}
              >
                <input
                  type='checkbox'
                  key='checkbox-all-users'
                  data-testid='checkbox-all-users'
                  className='form-checkbox h-5 w-5 text-blue-600'
                  checked={
                    selectedUsers.length === originallyAllowedUsers?.length
                  }
                  onChange={() => handleShareWithAllUsers()}
                />
                <h3
                  key='user-name-all-users'
                  className='text-base ml-2 break-all max-w-xs  mr-6'
                >
                  All users
                </h3>
              </label>
            </div>
            <br />

            <div className='flex items-start' key='div-main-all-firms'>
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <label
                className='flex items-center cursor-pointer'
                htmlFor='enable-tab'
                key='label-tab-all-firms'
                onClick={() => handleShareWithAllFirms()}
              >
                <input
                  type='checkbox'
                  key='checkbox-all-firms'
                  data-testid='checkbox-all-firms'
                  className='form-checkbox h-5 w-5 text-blue-600'
                  checked={
                    selectedFirms.length === originallyAllowedFirms?.length
                  }
                  onChange={() => handleShareWithAllFirms()}
                />
                <h3
                  key='share-with-all-firms'
                  className='text-base ml-2 break-all max-w-xs'
                >
                  All firms
                </h3>
              </label>
            </div>
          </div>
          <br />

          <br />

          <div className='overflow-y-auto'>
            <h3 className='mb-3 font-bold'>Firms</h3>
            {originallyAllowedFirms && originallyAllowedFirms?.length > 0
              ? originallyAllowedFirms?.map((firmClient) => (
                  <div key={`div-main-firm-${Number(firmClient?.firmId)}`}>
                    <div
                      className='flex items-start'
                      key={`div-sub-firm-${Number(firmClient?.firmId)}`}
                      id={`div-sub-firm-${Number(firmClient?.firmId)}`}
                    >
                      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                      <label
                        className='flex items-center cursor-pointer'
                        htmlFor='enable-tab'
                        key={`label-tab-firm-${Number(firmClient?.firmId)}`}
                        id={`label-tab-firm-${Number(firmClient?.firmId)}`}
                        onClick={() =>
                          addOrRemovefirms(Number(firmClient?.firmId))
                        }
                      >
                        <input
                          type='checkbox'
                          key={`checkbox-firm-${Number(firmClient?.firmId)}`}
                          data-testid={`checkbox-firm-${Number(
                            firmClient?.firmId
                          )}`}
                          className='form-checkbox h-5 w-5 text-blue-600'
                          checked={selectedFirms.includes(
                            Number(firmClient?.firmId)
                          )}
                          onChange={() =>
                            addOrRemovefirms(Number(firmClient?.firmId))
                          }
                        />
                        <h3
                          key={`firm-name-${Number(firmClient?.firmId)}`}
                          id={`firm-name-${Number(firmClient?.firmId)}`}
                          className='text-base ml-2 break-all max-w-xs'
                        >
                          {firmClient?.firmName}
                        </h3>
                      </label>
                    </div>
                    <br />
                  </div>
                ))
              : null}

            <h3 className='mb-3 font-bold'>Individual Users</h3>
            {originallyAllowedUsers && originallyAllowedUsers?.length > 0 ? (
              <>
                {originallyAllowedUsers.map((user) => (
                  <div key={`div-main-${user.user_id}`}>
                    <div
                      className='flex items-start'
                      key={`div-sub-${user.user_id}`}
                      id={`div-sub-${user.user_id}`}
                    >
                      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                      <label
                        className='flex items-center cursor-pointer'
                        htmlFor='enable-tab'
                        key={`label-tab-${user.user_id}`}
                        id={`label-tab-${user.user_id}`}
                        onClick={() => addOrRemoveUsers(user)}
                      >
                        <input
                          type='checkbox'
                          key={`checkbox-${user.user_id}`}
                          data-testid={`checkbox-${user.user_id}`}
                          className='form-checkbox h-5 w-5 text-blue-600'
                          checked={selectedUsers.includes(user.user_id)}
                          onChange={() => addOrRemoveUsers(user)}
                        />
                        <h3
                          key={`user-name-${user.user_id}`}
                          id={`user-name-${user.user_id}`}
                          className='text-base ml-2 break-all max-w-xs'
                        >
                          {user.user_name}
                        </h3>
                      </label>
                    </div>
                    <br />
                  </div>
                ))}
              </>
            ) : null}
            <br />
          </div>
          <div className='flex justify-center'>
            <Button
              type='submit'
              title='confirm'
              color='accent'
              size='sm'
              className='btn btn-accent'
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={saveData}
            >
              Send
              {isSaving && <Spinner className='mx-auto w-4 text-base ml-4' />}
            </Button>
          </div>
        </div>
      </>
    </GenericModal>
  );
}
